import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Megaphone, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Announcements() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany, companies } = useCompany();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [global, setGlobal] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", priority: "normal" });

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const { data } = await api.get("/announcements", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const submit = async () => {
    try {
      await api.post("/announcements", { ...form, company_id: global ? null : activeCompanyId });
      toast.success("Announcement posted");
      setOpen(false); setForm({ title: "", body: "", priority: "normal" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete announcement?")) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  const cmap = Object.fromEntries(companies.map((c) => [c.id, c]));

  return (
    <div data-testid="announcements-page">
      <PageHeader eyebrow={activeCompany?.code} title="Announcements" subtitle="Broadcast updates company-wide or globally.">
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="new-announcement-btn">
                <Plus size={14} className="mr-1.5" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-sm" data-testid="ann-title" /></div>
                <div><Label className="text-xs uppercase">Body</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} className="rounded-sm" data-testid="ann-body" /></div>
                <div><Label className="text-xs uppercase">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {user?.role === "super_admin" && (
                  <div className="flex items-center justify-between border border-border p-3 rounded-sm">
                    <div>
                      <div className="text-sm font-medium">Global broadcast</div>
                      <div className="text-xs text-[#64748B]">Send to all 5 companies</div>
                    </div>
                    <Switch checked={global} onCheckedChange={setGlobal} data-testid="ann-global" />
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="submit-ann">Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <PageBody>
        <Section title={`${items.length} announcements`}>
          <ul className="divide-y divide-border -m-5">
            {items.map((a) => (
              <li key={a.id} className="px-5 py-4 flex items-start gap-3" data-testid={`ann-${a.id}`}>
                <span className={`mt-1 inline-block w-1.5 h-1.5 ${a.priority === "urgent" ? "bg-[#F54236]" : a.priority === "high" ? "bg-[#FAB005]" : "bg-[#2563EB]"}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#0F172A]">{a.title}</span>
                    {!a.company_id && <span className="text-[9px] uppercase tracking-wider font-semibold border border-[#2563EB] text-[#2563EB] px-1.5 py-0.5 rounded-sm">Global</span>}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">{a.body}</div>
                  <div className="text-[10px] text-[#64748B] mt-2 font-mono">
                    {a.created_at?.slice(0, 10)} · {a.company_id ? cmap[a.company_id]?.name : "All companies"}
                  </div>
                </div>
                {canEdit && <button onClick={() => del(a.id)} className="p-1.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm" data-testid={`del-ann-${a.id}`}><Trash size={14} /></button>}
              </li>
            ))}
            {items.length === 0 && <li className="px-5 py-12 text-center text-sm text-[#64748B]">No announcements yet</li>}
          </ul>
        </Section>
      </PageBody>
    </div>
  );
}
